"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.providerMatchProviderV9Schema = exports.providerMatchProviderV9ClinicalKeywordsSchema = void 0;
const zod_1 = require("zod");
const patientRelationshipSchema = zod_1.z.union([
    zod_1.z.literal('new'),
    zod_1.z.literal('established')
]);
const visitMethodSchema = zod_1.z.union([
    zod_1.z.literal('in_person'),
    zod_1.z.literal('video'),
    zod_1.z.literal('phone')
]);
const visibilitySchema = zod_1.z.union([
    zod_1.z.literal('direct_book'),
    zod_1.z.literal('availability_only'),
    zod_1.z.literal('hidden')
]);
const providerMatchAppointmentEhrPurposeVisibilitiesSchema = zod_1.z.object({
    agent: visibilitySchema,
    consumer: visibilitySchema,
    referral: zod_1.z.union([visibilitySchema, zod_1.z.string()]).optional().nullable()
});
const providerMatchContactSchema = zod_1.z.object({
    contact_type: zod_1.z.string(),
    extension: zod_1.z.string().optional().nullable(),
    subtype: zod_1.z.unknown().optional(),
    value: zod_1.z.string()
});
const providerMatchDegreeSchema = zod_1.z.object({
    name: zod_1.z.string(),
    source: zod_1.z.unknown().optional(),
    source_url: zod_1.z.unknown().optional()
});
const providerMatchExternalSystemSchema = zod_1.z.object({
    provider_id: zod_1.z.string(),
    source_system: zod_1.z.string(),
    system_type: zod_1.z.string().optional().nullable()
});
const providerMatchMetadataSchema = zod_1.z.object({
    availability_last_updated: zod_1.z.string(),
    last_modified: zod_1.z.string(),
    last_updated: zod_1.z.string()
});
const providerMatchNetworkAffiliationSchema = zod_1.z.object({
    name: zod_1.z.string(),
    type: zod_1.z.string()
});
const providerMatchPracticeGroupSchema = zod_1.z.record(zod_1.z.unknown()).and(zod_1.z.object({
    id: zod_1.z.number(),
    name: zod_1.z.string()
}));
const providerMatchBoardCertificationSchema = zod_1.z.object({
    board_name: zod_1.z.string(),
    board_specialty: zod_1.z.string(),
    certification_type: zod_1.z.string(),
    certifying_board: zod_1.z.string(),
    rank: zod_1.z.number(),
    specialty_name: zod_1.z.string(),
    year_certified: zod_1.z.number()
});
const providerMatchClinicalKeywordSchema = zod_1.z.object({
    cui: zod_1.z.string(),
    name: zod_1.z.string()
});
const providerMatchClinicalKeywordsSchema = zod_1.z.object({
    promoted: zod_1.z.array(providerMatchClinicalKeywordSchema),
    searchable: zod_1.z.array(providerMatchClinicalKeywordSchema)
});
exports.providerMatchProviderV9ClinicalKeywordsSchema = providerMatchClinicalKeywordsSchema;
const providerMatchLicenseSchema = zod_1.z.object({
    expiration_date: zod_1.z.string(),
    expiration_year: zod_1.z.string(),
    issue_date: zod_1.z.string(),
    issue_year: zod_1.z.number(),
    number: zod_1.z.string(),
    source_url: zod_1.z.string(),
    state: zod_1.z.string(),
    status: zod_1.z.string()
});
const providerMatchSubSpecialtySchema = zod_1.z.object({
    eui: zod_1.z.string(),
    name: zod_1.z.string()
});
const providerMatchProviderReviewAggregateRatingSchema = zod_1.z.object({
    average_rating: zod_1.z.number(),
    created: zod_1.z.string(),
    distribution: zod_1.z
        .object({
        1: zod_1.z.object({
            rating_count: zod_1.z.number(),
            rating_percentage: zod_1.z.number(),
            review_count: zod_1.z.number()
        }),
        2: zod_1.z.object({
            rating_count: zod_1.z.number(),
            rating_percentage: zod_1.z.number(),
            review_count: zod_1.z.number()
        }),
        3: zod_1.z.object({
            rating_count: zod_1.z.number(),
            rating_percentage: zod_1.z.number(),
            review_count: zod_1.z.number()
        }),
        4: zod_1.z.object({
            rating_count: zod_1.z.number(),
            rating_percentage: zod_1.z.number(),
            review_count: zod_1.z.number()
        }),
        5: zod_1.z.object({
            rating_count: zod_1.z.number(),
            rating_percentage: zod_1.z.number(),
            review_count: zod_1.z.number()
        })
    })
        .optional(),
    last_modified: zod_1.z.string(),
    rating_count: zod_1.z.number(),
    review_count: zod_1.z.number(),
    sub_ratings: zod_1.z.array(zod_1.z.object({
        average_rating: zod_1.z.number(),
        metric: zod_1.z.string(),
        response_count: zod_1.z.number()
    }))
});
const providerMatchProviderReviewSchema = zod_1.z.object({
    author_display_name: zod_1.z.string(),
    body: zod_1.z.string(),
    rating: zod_1.z.number(),
    review_date: zod_1.z.string(),
    review_id: zod_1.z.number(),
    review_source: zod_1.z.string()
});
const providerMatchProviderReviewsSchema = zod_1.z.object({
    additional_urls: zod_1.z.array(zod_1.z.object({
        type: zod_1.z.string(),
        url: zod_1.z.string()
    })),
    aggregate_ratings: providerMatchProviderReviewAggregateRatingSchema
        .optional()
        .nullable(),
    reviews: zod_1.z.array(providerMatchProviderReviewSchema),
    source: zod_1.z.string(),
    source_url: zod_1.z.string()
});
const providerMatchTrainingSchema = zod_1.z.object({
    city: zod_1.z.string().nullable(),
    country: zod_1.z.string().nullable(),
    degree: zod_1.z.string().nullable(),
    field_of_study: zod_1.z.string().nullable(),
    graduation_date: zod_1.z.string().nullable(),
    graduation_year: zod_1.z.string().nullable(),
    name: zod_1.z.string().nullable(),
    rank: zod_1.z.number().nullable(),
    state: zod_1.z.string().nullable(),
    street1: zod_1.z.string().nullable(),
    street2: zod_1.z.string().nullable(),
    type: zod_1.z.string().nullable(),
    zip: zod_1.z.string().nullable()
});
const providerMatchProviderProfileStatusTransitionSchema = zod_1.z
    .record(zod_1.z.unknown())
    .and(zod_1.z.object({
    new_status: zod_1.z.string().optional().nullable(),
    previous_status: zod_1.z.string().optional().nullable(),
    timestamp: zod_1.z.string().optional().nullable(),
    user_id: zod_1.z.string().optional().nullable()
}));
const providerMatchAppointmentEhrVisitTypeMappingSchema = zod_1.z.object({
    book_as_type_id: zod_1.z.string(),
    book_as_type_name: zod_1.z.string().optional().nullable(),
    duration_minutes: zod_1.z.number(),
    generics_allowed: zod_1.z.boolean(),
    id: zod_1.z.string().optional().nullable(),
    source_system: zod_1.z.string(),
    visibilities: providerMatchAppointmentEhrPurposeVisibilitiesSchema,
    visit_method: visitMethodSchema
});
const providerMatchSpecialtySchema = zod_1.z.object({
    eui: zod_1.z.string(),
    name: zod_1.z.string(),
    subspecialties: zod_1.z.array(providerMatchSubSpecialtySchema).optional().nullable()
});
const providerMatchAppointmentEhrPurposeSchema = zod_1.z.object({
    ehr_data: zod_1.z
        .array(providerMatchAppointmentEhrVisitTypeMappingSchema)
        .optional()
        .nullable(),
    name: zod_1.z.string(),
    patient_relationship: patientRelationshipSchema
});
exports.providerMatchProviderV9Schema = zod_1.z.object({
    accepting_new_patients: zod_1.z.boolean(),
    access_program_status: zod_1.z.string(),
    affiliation: zod_1.z.unknown().optional(),
    age_groups_seen: zod_1.z.array(zod_1.z.string()),
    always_available_provider: zod_1.z.boolean(),
    appointment_ehr_purposes: zod_1.z.array(providerMatchAppointmentEhrPurposeSchema),
    availability_last_updated: zod_1.z.string().nullable(),
    board_certifications: zod_1.z.array(providerMatchBoardCertificationSchema),
    book_online_url: zod_1.z.string(),
    broadwayhealth_aco: zod_1.z.string(),
    broadwayhealth_aco_badge: zod_1.z.boolean(),
    clinical_contact: zod_1.z.string().nullable(),
    clinical_interests: zod_1.z.string().nullable(),
    clinical_keywords: providerMatchClinicalKeywordsSchema,
    clinical_trials: zod_1.z.array(zod_1.z.unknown()),
    contacts: zod_1.z.array(providerMatchContactSchema),
    current_status: zod_1.z.string(),
    default_timezone: zod_1.z.string(),
    degrees: zod_1.z.array(providerMatchDegreeSchema),
    departments: zod_1.z.unknown(),
    direct_book_capable: zod_1.z.boolean(),
    direct_book_pmac: zod_1.z.boolean(),
    direct_book_pmc: zod_1.z.boolean(),
    entity_type: zod_1.z.string().nullable(),
    external_id: zod_1.z.string(),
    external_systems: zod_1.z.array(providerMatchExternalSystemSchema),
    gender: zod_1.z.string(),
    graduate_education: zod_1.z.unknown(),
    grants: zod_1.z.array(zod_1.z.unknown()),
    gx: zod_1.z.object({
        max_age: zod_1.z.number().nullable(),
        min_age: zod_1.z.number().nullable()
    }),
    hospital_affiliations: zod_1.z.array(zod_1.z.string()),
    id: zod_1.z.number(),
    image_url: zod_1.z.string(),
    insurance_accepted: zod_1.z.array(zod_1.z.unknown()),
    is_primary_care: zod_1.z.boolean(),
    is_specialty_care: zod_1.z.boolean(),
    ksched_appointment_request_email: zod_1.z.array(zod_1.z.object({
        email: zod_1.z.string()
    })),
    languages: zod_1.z.array(zod_1.z.unknown()),
    legal: zod_1.z.unknown().optional(),
    license: zod_1.z.array(providerMatchLicenseSchema),
    manually_created: zod_1.z.boolean().nullable(),
    media_mentions: zod_1.z.array(zod_1.z.unknown()),
    medical_license: zod_1.z.unknown(),
    metadata: providerMatchMetadataSchema.nullable().optional(),
    multi_resource_scheduling: zod_1.z.unknown().optional(),
    name: zod_1.z.object({
        first: zod_1.z.string(),
        full: zod_1.z.string(),
        last: zod_1.z.string(),
        middle: zod_1.z.string().nullable().optional(),
        preferred: zod_1.z.string().nullable().optional()
    }),
    network_affiliations: zod_1.z.array(providerMatchNetworkAffiliationSchema),
    networks: zod_1.z.array(zod_1.z.string()),
    notes: zod_1.z.string(),
    npi: zod_1.z.string(),
    pmc_db_only_provider: zod_1.z.boolean().nullable(),
    practice_groups: zod_1.z.array(providerMatchPracticeGroupSchema),
    professional_statement: zod_1.z.string(),
    provider_is_employed: zod_1.z.boolean(),
    provider_type: zod_1.z.string(),
    provider_videos: zod_1.z.array(zod_1.z.unknown()),
    publications: zod_1.z.array(zod_1.z.unknown()),
    rating: zod_1.z.unknown(),
    ratings: zod_1.z.record(zod_1.z.unknown()),
    request_appointment_url: zod_1.z.string(),
    reviews: providerMatchProviderReviewsSchema.optional().nullable(),
    sched_interval_duration_days: zod_1.z.number().nullable(),
    sched_interval_same_day_minute_delay: zod_1.z.number().nullable(),
    sched_interval_start_days: zod_1.z.number().nullable(),
    sort_preferences: zod_1.z.object({
        networks: zod_1.z.number().optional()
    }),
    specialties: zod_1.z.array(providerMatchSpecialtySchema),
    status_transitions: zod_1.z.array(providerMatchProviderProfileStatusTransitionSchema),
    suffix: zod_1.z.unknown(),
    syndicate_to_google: zod_1.z.unknown(),
    telehealth: zod_1.z.boolean(),
    telehealth_badge: zod_1.z.boolean(),
    training: zod_1.z.array(providerMatchTrainingSchema),
    video_url: zod_1.z.string(),
    virtual_care_url: zod_1.z.string().nullable(),
    years_in_practice: zod_1.z.number()
});
//# sourceMappingURL=validators.js.map